// The Materials page's reads and list rules: ONE copy, used by the desktop Materials page
// (pages/MaterialEntryPage.jsx, MaterialCustodyCard) and the phone (mobile/screens/MaterialsScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". The stock actions are materialActions.js; one
// lot's read and its usage / notes are lots.js.
import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';
import { isTransferableMtn } from '../../utils/mtnLot';

const API_BASE = config.API_BASE_URL;

export const MATERIAL_CATEGORIES = Object.freeze(['Sheet Stock', 'Shape Stock']);

/** GET /materials: every material record. Throws "Failed to list materials: <status>". */
export async function loadMaterialList() {
  const res = await fetch(`${API_BASE}/materials`);
  if (!res.ok) throw new Error(`Failed to list materials: ${res.status}`);
  return res.json();
}

/** GET /materials/{id}: one material. Throws "Failed to get material detail: <status>". */
export async function loadMaterial(materialId) {
  const res = await fetch(`${API_BASE}/materials/${materialId}`);
  if (!res.ok) throw new Error(`Failed to get material detail: ${res.status}`);
  return res.json();
}

/** The list's filters, all but "In stock only" (applied last, so the list can say how many it hid):
 *  the category, REM- codes hidden, search in code / description, owner, type; a sheet's thickness and
 *  finish, a shape's profile, width, height and length. */
export function materialListCandidates(materials, {
  category = 'Sheet Stock', hideRemnants = true, search = '', owner = '', materialType = '',
  thickness = '', finish = '', profileShape = '', profileWidth = '', profileHeight = '', stockLength = '',
} = {}) {
  const term = String(search || '').toLowerCase();
  return (materials || []).filter((m) => {
    if (m.material_category !== category) return false;
    if (hideRemnants && m.material_code?.startsWith('REM-')) return false;
    const matchesSearch = (m.material_code || '').toLowerCase().includes(term) || (m.description || '').toLowerCase().includes(term);
    const matchesOwner = owner ? parseInt(owner, 10) === m.owner_customer_id : true;
    const matchesMaterialType = materialType ? m.material_type === materialType : true;
    if (category === 'Sheet Stock') {
      const matchesThickness = thickness ? m.thickness_id === parseInt(thickness, 10) : true;
      const matchesFinish = finish ? m.surface_finish === finish : true;
      return matchesSearch && matchesOwner && matchesMaterialType && matchesThickness && matchesFinish;
    }
    const matchesProfileShape = profileShape ? m.profile_shape === profileShape : true;
    const matchesProfileWidth = profileWidth ? m.profile_width === parseFloat(profileWidth) : true;
    const matchesProfileHeight = profileHeight ? m.profile_height === parseFloat(profileHeight) : true;
    const matchesStockLength = stockLength ? m.stock_length === parseFloat(stockLength) : true;
    return matchesSearch && matchesOwner && matchesMaterialType && matchesProfileShape && matchesProfileWidth && matchesProfileHeight && matchesStockLength;
  });
}

/** In stock = QoH above zero. */
export const isInStock = (m) => (Number(m?.stock_quantity) || 0) > 0;

/** On hand, reserved, inbound, the minimum; available = on hand − reserved; total available = on hand +
 *  inbound − reserved (the desktop's detail figures: whole numbers). */
export function materialFigures(m) {
  const int = (v) => parseInt(v, 10) || 0;
  const onHand = int(m?.stock_quantity);
  const reserved = int(m?.reserved_quantity);
  const inbound = int(m?.inbound_quantity);
  return { onHand, reserved, inbound, minimum: int(m?.minimum_available), available: onHand - reserved, totalAvailable: onHand + inbound - reserved };
}

/** GET /materials/{id}/inbound_orders (purchase_order_id, po_number, supplier, quantity_due,
 *  estimated_receive_date); null when unreadable. */
export async function loadInboundOrders(materialId) {
  try {
    const res = await fetch(`${API_BASE}/materials/${materialId}/inbound_orders`);
    if (!res.ok) throw new Error('Failed to fetch inbound order details.');
    return await res.json();
  } catch (err) {
    console.error(err);
    return null;
  }
}

/** An inbound order's date as the desktop shows it (a plain date, read at noon UTC so no zone moves it). */
export const inboundDateLabel = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString() : 'N/A');

/** GET /materials/{id}/transactions?start=&end= (created_at, quantity_before, delta, quantity_after, reason,
 *  user_name, job_master_num, po_number). Throws the API's sentence. */
export async function loadMaterialTransactions(materialId, { start = null, end = null } = {}) {
  const queryParams = new URLSearchParams();
  if (start) queryParams.set('start', start.toISOString());
  if (end) queryParams.set('end', end.toISOString());
  const res = await fetch(`${API_BASE}/materials/${materialId}/transactions?${queryParams.toString()}`);
  if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to load transaction history'));
  return res.json();
}

/** The last `days` days, ending now. */
export function lastDays(days, now = new Date()) {
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { start, end: new Date(now) };
}

/** GET /material_tracking?material_id={id}&limit=500: a material's lots (TRKs). Throws on a refusal. */
export async function loadMaterialLots(materialId) {
  const res = await fetch(`${API_BASE}/material_tracking?material_id=${materialId}&limit=500`);
  if (!res.ok) throw new Error(`Failed to load material MTNs: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** The Material Tracking tab's status filter (server side). */
export const TRACKING_STATUS_OPTIONS = Object.freeze([
  { value: 'all', label: 'All' },
  { value: 'on_hand', label: 'On hand (unused + active)' },
  { value: 'active', label: 'Active (in use)' },
  { value: 'unused', label: 'Unused (full)' },
  { value: 'exhausted', label: 'Exhausted (cut)' },
  { value: 'transferred', label: 'Transferred to another lot' },
  { value: 'returned', label: 'Returned to owner' },
  { value: 'flagged', label: 'Flagged (discrepancy)' },
  { value: 'reconciled', label: 'Reconciled' },
]);

/** GET /material_tracking?status=&supplier=&owner_customer_id=&limit=500: every lot (the Material Tracking
 *  tab). null when unreadable. */
export async function loadTrackingLots({ status = 'all', supplier = '', owner = '' } = {}) {
  try {
    const params = new URLSearchParams();
    if (status && status !== 'all') params.set('status', status);
    if (supplier) params.set('supplier', supplier);
    if (owner) params.set('owner_customer_id', owner);
    params.set('limit', String(TRACKING_LIMIT));
    const res = await fetch(`${API_BASE}/material_tracking?${params.toString()}`);
    if (!res.ok) throw new Error(`Failed to load tracking numbers: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('loadTrackingLots error:', err);
    return null;
  }
}

/** The tracking list's client-side filters: material type, thickness, finish, and the search box (TRK
 *  number, material code / description, heat #, PO, supplier, customer). */
export function filterLots(rows, { search = '', materialType = '', thicknessId = '', finish = '' } = {}) {
  const q = String(search || '').trim().toLowerCase();
  return (rows || []).filter((m) => {
    if (materialType && m.material_type !== materialType) return false;
    if (thicknessId && String(m.thickness_id) !== String(thicknessId)) return false;
    if (finish && m.surface_finish !== finish) return false;
    if (q) {
      const inSearch = (
        String(m.mtn_id).padStart(6, '0').includes(q)
        || (m.material_code || '').toLowerCase().includes(q)
        || (m.material_description || '').toLowerCase().includes(q)
        || (m.heat_number || '').toLowerCase().includes(q)
        || String(m.po_number || '').toLowerCase().includes(q)
        || (m.supplier || '').toLowerCase().includes(q)
        || (m.customer_name || '').toLowerCase().includes(q)
      );
      if (!inSearch) return false;
    }
    return true;
  });
}

/** The lot a transfer draws from when there is no choice: the only open lot's id (a string), else ''. */
export function onlyOpenLotId(lots) {
  const open = (lots || []).filter(isTransferableMtn);
  return open.length === 1 ? String(open[0].mtn_id) : '';
}

/** GET /material_tracking returns at most this many lots, newest first. */
export const TRACKING_LIMIT = 500;

/** Custody positions still open (material still at the vendor or owed back). */
export const openCustodyRows = (rows) => (rows || []).filter((c) => !c.closed_at);

/** GET /materials/{id}/custody: the material at vendors (free issue). Throws the API's sentence. */
export async function loadCustody(materialId) {
  const res = await fetch(`${API_BASE}/materials/${materialId}/custody`);
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

/** The transfer's destination filters as the transfer opens: the same type, thickness and finish as the
 *  material moving (any owner: a transfer moves the same steel to another owner's record). */
export const transferFilterDefaults = (m) => ({
  owner: '', type: m?.material_type || '', thickness: m?.thickness_id ? String(m.thickness_id) : '', finish: m?.surface_finish || '', search: '',
});

/** A transfer's possible destinations: every other material record that matches the filters (type,
 *  thickness, finish, owner, and the search in code / description). */
export function transferDestinations(materials, sourceMaterialId, filters = {}) {
  return (materials || []).filter((m) => {
    if (m.material_id === sourceMaterialId) return false;
    if (filters.type && m.material_type !== filters.type) return false;
    if (filters.thickness && m.thickness_id !== parseInt(filters.thickness, 10)) return false;
    if (filters.finish && m.surface_finish !== filters.finish) return false;
    if (filters.owner && m.owner_customer_id !== parseInt(filters.owner, 10)) return false;
    if (filters.search) {
      const term = filters.search.toLowerCase();
      if (!(m.material_code || '').toLowerCase().includes(term) && !(m.description || '').toLowerCase().includes(term)) return false;
    }
    return true;
  });
}

/** GET /materials/{id}/owner_copy: whether `ownerId` already has this material's steel (`twins`) and what a transfer
 *  that creates it would call the copy (`code`, `can_create`, else `reason`): jobq-api "A transfer creates its
 *  destination". null when the read fails, so the box is never offered on a guess. */
export async function loadOwnerCopy(materialId, ownerId) {
  try {
    const res = await fetch(`${API_BASE}/materials/${materialId}/owner_copy?owner_customer_id=${encodeURIComponent(ownerId)}`);
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}

/** An owner's name for a material row ("CustID n" when the customer isn't in the list). */
export function ownerName(customers, ownerId) {
  const c = (customers || []).find((x) => x.customer_id === ownerId);
  return c ? c.name : (ownerId ? `CustID ${ownerId}` : '—');
}
